import { Component, effect, input, Input, output } from '@angular/core';
import { EnvService } from 'src/services/env.service';

/**
 * Credentials interface for type-safe form submission.
 */
export interface CxLoginCredentials {
	accountId: string;
	loginName: string;
	password: string;
}

/**
 * Hidden form component for CX (ComplianceExpress) authentication.
 * This component provides a reusable hidden form that submits credentials to the legacy CX backend.
 * Form values are set programmatically to avoid XSS vulnerabilities from template interpolation.
 *
 * Uses signal-based reactive pattern for form submission - parent components trigger submission
 * by updating the submitRequest signal input.
 */
@Component({
	selector: 'app-cx-hidden-login-form',
	templateUrl: './cx-hidden-login-form.component.html'
})
export class CxHiddenLoginFormComponent {
	/**
	 * Unique ID for the hidden form element. Allows multiple instances on the same page.
	 */
	@Input() formId = 'cxLoginSubmissionForm';

	/**
	 * Route name to redirect to after successful authentication.
	 */
	@Input() redirectRoute!: string;

	/**
	 * Array of redirect parameters to pass back after authentication (e.g., ['inviteId=123', 'token=abc']).
	 */
	@Input() redirectParams: string[] = [];

	/**
	 * Whether to include the 'remember me' checkbox in the form submission.
	 * Used by AgencyLogin for persistent login sessions.
	 */
	@Input() rememberMe = false;

	/**
	 * Signal input for triggering form submission reactively.
	 * Parent components set this to a credentials object to trigger submission.
	 * Set to null when no submission is pending.
	 */
	submitRequest = input<CxLoginCredentials | null>(null);

	/**
	 * Emits when form submission completes successfully.
	 * Parent components can use this to track submission state.
	 */
	submissionComplete = output<void>();

	/**
	 * Emits when form submission fails with an error.
	 * Parent components should handle errors gracefully and reset UI state.
	 */
	submissionError = output<Error>();

	constructor(protected env: EnvService) {
		// Automatically cleans up on component destruction
		effect(() => {
			const request = this.submitRequest();
			if (request) {
				try {
					this.performSubmit(request);
					this.submissionComplete.emit();
				} catch (error) {
					this.submissionError.emit(error as Error);
				}
			}
		});
	}

	/**
	 * Performs the actual form submission with provided credentials.
	 * Sets form values programmatically to avoid XSS vulnerability from template interpolation.
	 *
	 * @param credentials - Login credentials object
	 * @throws Error if form element not found in DOM
	 * @throws Error if required form inputs not found
	 */
	private performSubmit(credentials: CxLoginCredentials): void {
		const form = document.getElementById(this.formId) as HTMLFormElement;
		if (!form) {
			throw new Error(`CX login form with id "${this.formId}" not found`);
		}

		// Set values programmatically (NOT via template interpolation for security)
		const accountIdInput = form.querySelector('input[name="accountId"]') as HTMLInputElement | null;
		const loginNameInput = form.querySelector('input[name="loginName"]') as HTMLInputElement | null;
		const passwordInput = form.querySelector('input[name="password"]') as HTMLInputElement | null;

		if (!accountIdInput || !loginNameInput || !passwordInput) {
			throw new Error('Required form inputs not found in hidden CX form');
		}

		accountIdInput.value = credentials.accountId;
		loginNameInput.value = credentials.loginName;
		passwordInput.value = credentials.password;

		form.submit();
	}
}
